import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ListChecks, Plus, Loader2, Copy, CheckCircle2, AlertTriangle, Ban, RotateCcw } from 'lucide-react';
import { copiarTexto } from '../dashboard/rotina';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Acessos de conferência (Adnéia, 26/10/2026), só para a gestão (admin).
// Quem não usa o ELO (o Matheus, da logística) confere pelo Claude dele se uma lista
// de entregas tem atendimento, olhando o pedido inteiro. O acesso não abre a tela do
// ELO: só responde "tem / não tem". Nasce sem senha; a pessoa recebe o link de nova
// senha e cria a dela. Backend: routes/conferencia.py.
const AcessosConferencia = () => {
  const { getAuthHeader } = useAuth();
  const [acessos, setAcessos] = useState(null); // null = carregando
  const [erro, setErro] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [criado, setCriado] = useState(null); // resposta da criação

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/conferencia/acessos`, { headers: getAuthHeader() });
      setAcessos(r.data?.acessos || []);
      setErro('');
    } catch (e) {
      setAcessos([]);
      setErro(e.response?.data?.detail || 'Não consegui carregar os acessos.');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const criar = async () => {
    setSalvando(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/conferencia/acessos`,
        { nome, email },
        { headers: getAuthHeader() }
      );
      setCriado(r.data);
      setNome('');
      setEmail('');
      if (r.data?.enviado) {
        toast.success(`Acesso criado. Link de senha mandado para ${r.data.para}`);
      } else {
        toast.warning('Acesso criado, mas o e-mail não saiu.');
      }
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não consegui criar o acesso.');
    } finally {
      setSalvando(false);
    }
  };

  const trocar = async (a, acao) => {
    setSalvando(true);
    try {
      await axios.post(`${API_URL}/api/conferencia/acessos/${a.id}/${acao}`, {}, { headers: getAuthHeader() });
      toast.success(acao === 'desativar' ? `Acesso de ${a.name} desativado` : `Acesso de ${a.name} reativado`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não consegui mudar o acesso.');
    } finally {
      setSalvando(false);
    }
  };

  const copiar = (link) => {
    copiarTexto(
      link,
      () => toast.success('Link copiado'),
      () => toast.error('Não consegui copiar. Selecione o link e copie.')
    );
  };

  return (
    <Card data-testid="acessos-conferencia">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ListChecks className="h-5 w-5" />
          Acessos de conferência
        </CardTitle>
        <CardDescription>
          Para quem não usa o ELO conferir pelo Claude se uma lista de entregas tem atendimento
          (olha o pedido inteiro, todas as entregas). O acesso não abre o ELO e não vê chamado nem
          cliente: só responde "tem / não tem". A pessoa recebe por e-mail o link para criar a senha.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {acessos === null && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
          </p>
        )}
        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
        {acessos && acessos.length === 0 && !erro && (
          <p className="text-sm text-muted-foreground">Nenhum acesso de conferência ainda.</p>
        )}
        {(acessos || []).map((a) => (
          <div key={a.id} className="rounded-md border p-3 flex items-center justify-between gap-3 flex-wrap"
               data-testid={`acesso-conferencia-${a.id}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">
                {a.name}
                {!a.ativo && (
                  <span className="ml-2 text-xs rounded bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 text-muted-foreground">
                    desativado
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground truncate">{a.email}</p>
            </div>
            {a.ativo ? (
              <Button variant="outline" size="sm" disabled={salvando} onClick={() => trocar(a, 'desativar')}>
                <Ban className="h-4 w-4 mr-2" />
                Desativar
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled={salvando} onClick={() => trocar(a, 'reativar')}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reativar
              </Button>
            )}
          </div>
        ))}

        <div className="space-y-2 rounded-md bg-slate-50 dark:bg-slate-900 p-3">
          <p className="text-sm font-medium">Novo acesso</p>
          <Label htmlFor="conf-nome">Nome</Label>
          <Input id="conf-nome" value={nome} onChange={(e) => setNome(e.target.value)} disabled={salvando} />
          <Label htmlFor="conf-email">E-mail da casa (sem conta no ELO)</Label>
          <Input id="conf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 disabled={salvando} />
          <Button size="sm" onClick={criar} disabled={salvando || !nome.trim() || !email.trim()}>
            {salvando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Criar e mandar o link de senha
          </Button>
        </div>

        {criado && criado.enviado && (
          <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Acesso de {criado.nome} criado. Link de senha mandado para {criado.para} (vale {criado.validade_horas} horas).
          </p>
        )}
        {criado && !criado.enviado && (
          <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
            <p className="text-sm text-amber-900 dark:text-amber-100 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {criado.link
                ? `Acesso criado, mas o e-mail não saiu. Mande este link a ${criado.nome} (vale ${criado.validade_horas} horas, uma vez só):`
                : `Acesso criado, mas o link não saiu (${criado.erro || 'erro'}). Use "Mandar link de nova senha" em Senhas da equipe.`}
            </p>
            {criado.link && (
              <div className="flex gap-2">
                <Input readOnly value={criado.link} onFocus={(e) => e.target.select()} className="text-xs" />
                <Button size="sm" variant="outline" onClick={() => copiar(criado.link)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AcessosConferencia;
